import React, { useState } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { signInWithOAuthNative } from '../../lib/nativeOAuth';
import { ChevronLeft } from 'lucide-react';
import LanguageSelector from '../LanguageSelector';
import { Capacitor } from '@capacitor/core';
import { useLanguageContext } from '../../contexts/LanguageContext';

interface LoginScreenProps {
  onBack: () => void;
}

const LoginScreen: React.FC<LoginScreenProps> = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const isNative = Capacitor.isNativePlatform();
  const { language } = useLanguageContext();

  const handleOAuthLogin = async (provider: 'google' | 'apple') => {
    setOauthLoading(true);
    setError(null);
    try {
      if (isNative) {
        const { error } = await signInWithOAuthNative(provider, language);
        if (error) throw error;
      } else {
        const { error } = await supabase.auth.signInWithOAuth({
          provider,
          options: {
            redirectTo: `${window.location.origin}?lang=${language}`,
            queryParams: provider === 'google' ? {
              access_type: 'offline',
              prompt: 'consent',
            } : undefined,
          },
        });
        if (error) throw error;
      }
    } catch (err: any) {
      console.error(`❌ Erreur ${provider} OAuth:`, err);
      setError(err.message || `Erreur de connexion ${provider}`);
      setOauthLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // Success: App.tsx will handle redirect via onAuthStateChange
      console.log('✅ Connexion réussie');
    } catch (err: any) {
      console.error('❌ Erreur de connexion:', err);
      setError(err.message || 'Erreur de connexion');
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Veuillez entrer votre adresse email');
      return;
    }

    setResetLoading(true);
    setError(null);

    try {
      // Note: resetPasswordForEmail doesn't support data option
      // We pass lang in redirectTo URL for the landing page to know the language
      // The email template will use the user's stored lang from user_metadata (set at signup)
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password?lang=${language}`,
      });

      if (error) throw error;

      setResetSent(true);
    } catch (err: any) {
      console.error('❌ Erreur reset password:', err);
      setError(err.message || 'Erreur lors de l\'envoi');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="h-screen-adaptive bg-gradient-to-b from-purple-100 to-pink-100 flex items-center justify-center relative p-4">
      {/* Language Selector - Top Right */}
      <div className={`absolute right-4 z-10 ${isNative ? 'top-0 safe-area-top pt-2' : 'top-4'}`}>
        <LanguageSelector />
      </div>

      <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-6 transition-colors"
        >
          <ChevronLeft size={20} />
          Retour
        </button>

        <h2 className="text-2xl font-bold text-center mb-6 font-title">
          {resetMode ? 'Réinitialiser le mot de passe' : 'Connexion'}
        </h2>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {resetSent ? (
          <div className="text-center">
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
              Un email de réinitialisation a été envoyé à <strong>{email}</strong>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Vérifiez votre boîte mail et cliquez sur le lien pour créer un nouveau mot de passe.
            </p>
            <button
              onClick={() => {
                setResetMode(false);
                setResetSent(false);
                setEmail('');
              }}
              className="text-blue-600 hover:underline"
            >
              Retour à la connexion
            </button>
          </div>
        ) : resetMode ? (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <p className="text-sm text-gray-600 mb-4">
              Entrez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="votre@email.com"
                required
                disabled={resetLoading}
              />
            </div>
            <button
              type="submit"
              disabled={resetLoading}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resetLoading ? 'Envoi en cours...' : 'Envoyer le lien'}
            </button>
            <p className="text-sm text-gray-600 text-center">
              <button
                type="button"
                onClick={() => {
                  setResetMode(false);
                  setError(null);
                }}
                className="text-blue-600 hover:underline"
              >
                Retour à la connexion
              </button>
            </p>
          </form>
        ) : (
          <>
        {/* Google OAuth Button */}
        <button
          onClick={() => handleOAuthLogin('google')}
          disabled={oauthLoading || loading}
          className="w-full bg-white text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors shadow-md border border-gray-300 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed mb-3"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {oauthLoading ? 'Connexion...' : 'Continuer avec Google'}
        </button>

        {/* Apple OAuth Button */}
        <button
          onClick={() => handleOAuthLogin('apple')}
          disabled={oauthLoading || loading}
          className="w-full bg-black text-white py-3 rounded-lg font-medium hover:bg-gray-900 transition-colors shadow-md flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed mb-3"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
          </svg>
          {oauthLoading ? 'Connexion...' : 'Continuer avec Apple'}
        </button>

        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-white px-2 text-gray-500">ou par email</span>
          </div>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="votre@email.com"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mot de passe
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="••••••••"
              required
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Connexion...' : 'Se connecter'}
          </button>
        </form>

        <p className="text-sm text-gray-600 text-center mt-4">
          Mot de passe oublié ?{' '}
          <button
            onClick={() => {
              setResetMode(true);
              setError(null);
            }}
            className="text-blue-600 hover:underline"
          >
            Réinitialiser
          </button>
        </p>
          </>
        )}

        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-600 text-center">
            Pas encore de compte ?{' '}
            <button
              onClick={onBack}
              className="text-green-600 hover:underline font-medium"
            >
              Créer un compte
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
